import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverFooter,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import validator from 'validator';
import { AuthContext } from '../../../../shared/providers';
import { getEntityInstanceFromDetection } from '../../../../shared/utils';
import { useEntitiesContext, EntityInstance } from '../../context';
import { EntityPopoverButton } from './EntityPopoverButton';
import { EntityPopoverHeader } from './EntityPopoverHeader';
import { EntityPopoverBody } from './EntityPopoverBody';
import { EntityActionsMenu } from './EntityActionsMenu';
import { DetectionSummary } from '@investigator/shared/generated/graphql';

enum DATA_TEST_IDS {
  TriggerLabel = 'entity-information-trigger-label',
  TriggerPopoverBtn = 'entity-information-trigger-popover-btn',
  TriggerPopoverContent = 'entity-information-trigger-popover-content',
  DetailsFooter = 'entity-information-trigger-details-footer',
}

interface EntityAnalysisPopoverProps {
  isStandalone?: boolean;
  detectionSummary: DetectionSummary;
  /** If true, only allow suppression when entity has exactly 1 detection */
  requireSingleDetectionForSuppression?: boolean;
  start: number;
  end: number;
}

export const EntityAnalysisPopover = memo(
  ({
    isStandalone,
    detectionSummary,
    requireSingleDetectionForSuppression,
    start,
    end,
  }: EntityAnalysisPopoverProps) => {
    const { user } = useContext(AuthContext);
    const tenantIdFromAuth = user?.attributes?.['custom:tenant_id'];
    const safeTenantId =
      detectionSummary?.tenant_info?.tenant_id || tenantIdFromAuth;
    const {
      selectedEntity,
      setSelectedEntity,
      analysisModal: { isOpen: isModalOpen, onOpen: onEntityModalOpen },
      suppression,
      suppressionModal: {
        isOpen: isSuppressionModalOpen,
        onOpen: onSuppressOpen,
      },
      isolationModal: { isOpen: isIsolationModalOpen },
    } = useEntitiesContext();

    // Local UI state
    const [contextMenuOpen, setContextMenuOpen] = useState<boolean>(false);
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const stackRef = useRef<HTMLDivElement>(null);
    const isThisEntityActive = useMemo(
      () => selectedEntity?.entityId === detectionSummary?.entity_id,
      [selectedEntity?.entityId, detectionSummary?.entity_id],
    );

    const anyModalOpen = useMemo(
      () => isModalOpen || isSuppressionModalOpen || isIsolationModalOpen,
      [isModalOpen, isSuppressionModalOpen, isIsolationModalOpen],
    );

    // This prevents all popovers with the same entity_id from opening simultaneously
    const isPopoverOpen = useMemo(
      () =>
        isHovered && // LOCAL: this specific instance is hovered
        isThisEntityActive && // CONTEXT: data has been fetched for this entity
        !anyModalOpen &&
        !contextMenuOpen,
      [isHovered, isThisEntityActive, anyModalOpen, contextMenuOpen],
    );

    // Computed loading state for button
    // Show loading when hovered but data isn't ready yet
    const isButtonLoading = useMemo(
      () =>
        isHovered &&
        isThisEntityActive &&
        (selectedEntity?.detectionDataList === undefined ||
          suppression.loading),
      [
        isHovered,
        isThisEntityActive,
        selectedEntity?.detectionDataList,
        suppression.loading,
      ],
    );

    // Mouse enter - set local hover state immediately, fetch data via context
    const handleMouseEnter = useCallback(
      (_event: React.MouseEvent<HTMLDivElement>) => {
        setContextMenuOpen(false);
        setIsHovered(true);
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }

        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
          searchTimeoutRef.current = null;
        }

        if (anyModalOpen) {
          return;
        }

        // Delay the search query by .5 seconds
        searchTimeoutRef.current = setTimeout(() => {
          if (selectedEntity?.entityId !== detectionSummary?.entity_id) {
            const newEntity: EntityInstance = {
              ...getEntityInstanceFromDetection(detectionSummary),
              end,
              start,
              requireSingleDetectionForSuppression,
            };

            setSelectedEntity(newEntity);
          }
        }, 500);
      },
      [
        setContextMenuOpen,
        setSelectedEntity,
        anyModalOpen,
        detectionSummary,
        selectedEntity?.entityId,
        requireSingleDetectionForSuppression,
        start,
        end,
      ],
    );

    const handleMouseLeave = useCallback(() => {
      // Clear local hover state with a small delay to allow moving to popover content
      const thisEntityId = detectionSummary?.entity_id;
      hoverTimeoutRef.current = setTimeout(() => {
        setIsHovered(false);
        // Clear selectedEntity if no modal is open
        if (!anyModalOpen) {
          setSelectedEntity((prev) => {
            if (prev?.entityId === thisEntityId) {
              return undefined;
            }
            return prev;
          });
        }
      }, 200);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
    }, [anyModalOpen, setSelectedEntity, detectionSummary?.entity_id]);

    const handleClick = useCallback(() => {
      const tenantQuery = safeTenantId
        ? `,{"terms":{"tenant":["${safeTenantId}"]}}`
        : '';

      const excludedEntitiesQuery =
        detectionSummary.entity_type === 'DOMAIN'
          ? `{"query": {"bool": {"must": [{"match": {"content_id": "${detectionSummary.content_id}"}}, {"term": {"entity_name.keyword": "${detectionSummary.entity_name}"}}, {"term": {"entity_type": "${detectionSummary.entity_type}"}}${tenantQuery}]}}}`
          : `{"query": {"bool": {"must": [{"match": {"content_id": "${detectionSummary.content_id}"}}, {"term": {"entity_type": "${detectionSummary.entity_type}"}}, {"bool": {"should": [{"term": {"entity_ip": "${detectionSummary.entity_name}"}}, {"range": {"entity_ip_range": {"from": "${detectionSummary.entity_name}", "to": "${detectionSummary.entity_name}"}}}]}}${tenantQuery}]}}}`;

      const entityForModal: EntityInstance = {
        ...getEntityInstanceFromDetection(detectionSummary),
        end,
        start,
        excludedEntitiesVariables: detectionSummary.content_id
          ? {
              query: excludedEntitiesQuery,
              offset: 0,
              size: 10,
              sort: [{ sort_by: 'entity_name.keyword', sort_dir: 'desc' }],
            }
          : undefined,
        requireSingleDetectionForSuppression,
      };

      // Pass entity to modal open handler to ensure it's set in context
      onEntityModalOpen(entityForModal);
      setContextMenuOpen(false);
    }, [
      detectionSummary,
      requireSingleDetectionForSuppression,
      safeTenantId,
      start,
      end,
      onEntityModalOpen,
    ]);

    const handleSuppress = useCallback(() => {
      // selectedEntity is already set by handleMouseEnter with timeline data
      // Don't overwrite detectionData here - it has the correct fields from the timeline query
      onSuppressOpen();
      setContextMenuOpen(false);
    }, [onSuppressOpen, setContextMenuOpen]);

    const handlePopoverContentMouseEnter = useCallback(() => {
      // Keep popover open when mouse enters content
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      setIsHovered(true);
    }, []);

    const handlePopoverContentMouseLeave = useCallback(() => {
      // Close popover when mouse leaves content
      const thisEntityId = detectionSummary?.entity_id;
      hoverTimeoutRef.current = setTimeout(() => {
        setIsHovered(false);
        if (!anyModalOpen) {
          setSelectedEntity((prev) => {
            if (prev?.entityId === thisEntityId) {
              return undefined;
            }
            return prev;
          });
        }
      }, 200);
    }, [anyModalOpen, setSelectedEntity, detectionSummary?.entity_id]);

    const detectionsPivotParams = useMemo(() => {
      const category = detectionSummary?.entity_category ?? 'source';
      const categoryParam =
        category === 'destination' ? 'destination_name' : 'source_name';

      return `start=${start}&end=${end}&${categoryParam}=${detectionSummary.entity_name}${safeTenantId ? `&tenant_id=${String(safeTenantId)}` : ''}`;
    }, [detectionSummary, start, end, safeTenantId]);

    // Cleanup hover timeout on unmount
    useEffect(() => {
      return () => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
      };
    }, []);

    // Close popover when any modal opens
    useEffect(() => {
      if (anyModalOpen) {
        setIsHovered(false);
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
      }
    }, [anyModalOpen]);

    // Only render interactive popover for IP addresses
    // For non-IP entities (domains, usernames, etc.), just render plain text
    if (!validator.isIP(detectionSummary?.entity_name || '')) {
      return (
        <Text px={2} py={1} data-testid={DATA_TEST_IDS.TriggerLabel}>
          {detectionSummary?.entity_name}
        </Text>
      );
    }

    return (
      <Popover
        isLazy
        offset={[0, 0]}
        placement="bottom-start"
        isOpen={isPopoverOpen}
        onClose={() => {
          setIsHovered(false);
          if (!anyModalOpen) {
            setSelectedEntity(undefined);
          }
        }}
      >
        <PopoverAnchor>
          <VStack
            ref={stackRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <EntityPopoverButton
              entityName={detectionSummary?.entity_name ?? ''}
              isStandalone={isStandalone}
              contextMenuOpen={contextMenuOpen}
              isHoveredEntity={isHovered}
              handleEntityClick={handleClick}
              setContextMenuOpen={setContextMenuOpen}
              handleSuppressEntity={handleSuppress}
              isSuppressed={!!selectedEntity?.entityIsSuppressed}
              isDisabled={!suppression.enabled}
              isLoadingSuppression={isButtonLoading}
              start={start}
              end={end}
              data-testid={DATA_TEST_IDS.TriggerPopoverBtn}
            />
          </VStack>
        </PopoverAnchor>
        {isPopoverOpen && (
          <Portal>
            <PopoverContent
              w="400px"
              borderColor="layer.3"
              data-testid={DATA_TEST_IDS.TriggerPopoverContent}
              onMouseEnter={handlePopoverContentMouseEnter}
              onMouseLeave={handlePopoverContentMouseLeave}
            >
              <EntityPopoverHeader />
              <EntityPopoverBody
                detectionsPivotParams={detectionsPivotParams}
              />
              <PopoverFooter
                border={0}
                alignItems="end"
                data-testid={DATA_TEST_IDS.DetailsFooter}
              >
                <EntityActionsMenu onClose={() => setContextMenuOpen(false)} />
              </PopoverFooter>
            </PopoverContent>
          </Portal>
        )}
      </Popover>
    );
  },
);

EntityAnalysisPopover.displayName = 'EntityAnalysisPopover';
